import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  User,
  Mail,
  MailCheck,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  ShieldCheck,
  Zap,
  TrendingDown,
  CheckCircle2,
} from "lucide-react";

const TID = {
  pageTitle: "login-page-title",
  inputEmail: "login-input-email",
  inputPassword: "login-input-password",
  togglePassword: "login-toggle-password",
  rememberCheckbox: "login-remember-checkbox",
  forgotLink: "login-forgot-link",
  submitButton: "login-submit-button",
  successMessage: "login-success-message",
  signupLink: "login-signup-link",
};

const perks = [
  { icon: Zap, t: "One-click ordering", b: "Resume your last basket in a single click." },
  { icon: TrendingDown, t: "Live savings tracker", b: "See month-to-date savings the second you log in." },
  { icon: ShieldCheck, t: "Bank-grade security", b: "Encrypted in transit & at rest. SOC-2 standards." },
];

const fadeInitial = { opacity: 0, y: 24 };
const fadeAnimate = { opacity: 1, y: 0 };
const fadeTransition = { duration: 0.5 };
const brandTransition = { duration: 0.6, delay: 0.1 };

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPwd, setShowPwd] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoverySent, setRecoverySent] = useState(false);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recovery, setRecovery] = useState({ username: "", email: "" });
  const [recoveryErrors, setRecoveryErrors] = useState({});

  const onSubmit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!email.trim()) er.email = "Required";
    if (!password) er.password = "Required";
    else if (password.length < 6) er.password = "Must be at least 6 characters";
    setErrors(er);
    if (Object.keys(er).length) return;
    setLoading(true);
    await new Promise((r) => setTimeout(r, 900));
    setLoading(false);
    setSuccess(true);
  };

  const openRecovery = () => {
    setRecoveryOpen(true);
    setRecoverySent(false);
    setRecoveryErrors({});
  };

  const closeRecovery = () => {
    if (recoveryLoading) return;
    setRecoveryOpen(false);
    setRecoverySent(false);
    setRecoveryErrors({});
  };

  const onRecoverySubmit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!recovery.username.trim()) er.username = "Required";
    if (!recovery.email.trim()) er.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recovery.email)) er.email = "Enter a valid email";
    setRecoveryErrors(er);
    if (Object.keys(er).length) return;
    setRecoveryLoading(true);
    await new Promise((r) => setTimeout(r, 800));
    setRecoveryLoading(false);
    setRecoverySent(true);
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] bg-white">
      <div className="grid lg:grid-cols-2 min-h-[calc(100vh-5rem)]">
        {/* LEFT — Form */}
        <div className="flex items-center justify-center px-4 sm:px-6 lg:px-12 py-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-radial-emerald" />
          <div className="absolute inset-0 bg-grid opacity-40" />

          <motion.div
            initial={fadeInitial}
            animate={fadeAnimate}
            transition={fadeTransition}
            className="relative w-full max-w-md"
          >
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Customer Portal</div>
            <h1
              data-testid={TID.pageTitle}
              className="text-4xl lg:text-5xl font-bold text-[#0A2540] tracking-tight leading-[1.05] mb-3"
            >
              Welcome back.
            </h1>
            <p className="text-slate-600 mb-10">Sign in to your Cascade Plus account to manage orders, view order history and reports.</p>

            {success ? (
              <div data-testid={TID.successMessage} className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center">
                <div className="h-14 w-14 mx-auto rounded-full bg-white flex items-center justify-center mb-4 shadow-sm">
                  <CheckCircle2 className="h-7 w-7 text-emerald-500" />
                </div>
                <h3 className="text-xl font-bold text-[#0A2540] mb-2">Signed in successfully</h3>
                <p className="text-sm text-slate-600">Redirecting you to your dashboard…</p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" noValidate>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#0A2540] mb-2">
                    Username
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                    <input
                      id="email"
                      type="text"
                      autoComplete="username"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errors.email) setErrors((er) => ({ ...er, email: undefined }));
                      }}
                      data-testid={TID.inputEmail}
                      placeholder="Enter your username"
                      className={`w-full rounded-xl border pl-11 pr-4 py-3 text-[#0A2540] placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all ${
                        errors.email
                          ? "border-rose-300 focus:ring-rose-200 focus:border-rose-400"
                          : "border-slate-200 focus:ring-emerald-500 focus:border-emerald-500"
                      }`}
                    />
                  </div>
                  {errors.email && <p className="mt-1.5 text-xs text-rose-600 font-medium">{errors.email}</p>}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="password" className="block text-sm font-semibold text-[#0A2540]">
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={openRecovery}
                      data-testid={TID.forgotLink}
                      className="text-xs font-semibold text-emerald-600 hover:text-emerald-700"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                    <input
                      id="password"
                      type={showPwd ? "text" : "password"}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (errors.password) setErrors((er) => ({ ...er, password: undefined }));
                      }}
                      data-testid={TID.inputPassword}
                      placeholder="Enter your password"
                      className={`w-full rounded-xl border pl-11 pr-11 py-3 text-[#0A2540] placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all ${
                        errors.password
                          ? "border-rose-300 focus:ring-rose-200 focus:border-rose-400"
                          : "border-slate-200 focus:ring-emerald-500 focus:border-emerald-500"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      data-testid={TID.togglePassword}
                      aria-label={showPwd ? "Hide password" : "Show password"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                    >
                      {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password && <p className="mt-1.5 text-xs text-rose-600 font-medium">{errors.password}</p>}
                </div>

                <label className="flex items-center gap-2.5 select-none cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    data-testid={TID.rememberCheckbox}
                    className="h-4 w-4 rounded border-slate-300 text-emerald-500 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-slate-600 group-hover:text-[#0A2540]">Keep me signed in on this device</span>
                </label>

                <button
                  type="submit"
                  data-testid={TID.submitButton}
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#0A2540] text-white px-6 py-4 rounded-xl font-semibold hover:bg-[#0F365C] transition-all hover:gap-3 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg"
                >
                  {loading ? "Signing in..." : "Sign in to your account"}
                  <ArrowRight className="h-4 w-4" />
                </button>

                <div className="relative py-2">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-3 bg-white text-xs font-semibold text-slate-400 uppercase tracking-wider">or</span>
                  </div>
                </div>

                <p className="text-center text-sm text-slate-600">
                  Not a customer yet?{" "}
                  <Link
                    to="/contact"
                    data-testid={TID.signupLink}
                    className="font-semibold text-emerald-600 hover:text-emerald-700"
                  >
                    Request access — it's free
                  </Link>
                </p>
              </form>
            )}
          </motion.div>
        </div>

        {/* RIGHT — Brand panel */}
        <div className="relative hidden lg:flex items-center justify-center bg-[#0A2540] overflow-hidden">
          <div className="absolute inset-0 bg-grid opacity-[0.06]" />
          <div className="absolute -top-32 -left-20 h-96 w-96 bg-emerald-500/20 blur-3xl rounded-full" />
          <div className="absolute -bottom-32 -right-20 h-96 w-96 bg-sky-500/10 blur-3xl rounded-full" />

          <motion.div
            initial={fadeInitial}
            animate={fadeAnimate}
            transition={brandTransition}
            className="relative max-w-md px-10 py-16 text-white"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-emerald-300 text-xs font-semibold mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Live ordering platform
            </div>
            <h2 className="text-4xl font-bold tracking-tight leading-tight mb-4">
              Your buy-side, in <span className="text-emerald-400">one place.</span>
            </h2>
            <p className="text-slate-300 leading-relaxed mb-10">
              Manage out-of-stock lines, transmit orders to every wholesaler, and track your savings — all from one independent platform.
            </p>

            <div className="space-y-5">
              {perks.map((p) => (
                <div key={p.t} className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center shrink-0">
                    <p.icon className="h-4 w-4 text-emerald-400" />
                  </div>
                  <div>
                    <div className="font-semibold text-white">{p.t}</div>
                    <div className="text-sm text-slate-400">{p.b}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 rounded-2xl bg-white/5 border border-white/10 p-5">
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-3xl font-bold text-white">&pound;1,000+</span>
                <span className="text-sm text-emerald-400 font-semibold">/month avg</span>
              </div>
              <p className="text-xs text-slate-400">saved by Cascade Plus customers</p>
            </div>
          </motion.div>
        </div>
      </div>
      {recoveryOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-900/55 backdrop-blur-sm" onClick={closeRecovery} />
          <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
          >
            {!recoverySent ? (
              <>
                <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-2">Password recovery</div>
                    <h2 className="text-2xl font-bold text-[#0A2540]">Reset your password</h2>
                    <p className="mt-2 text-sm text-slate-500">Enter your details and we'll send a new password to your email.</p>
                  </div>
                  <button
                    type="button"
                    aria-label="Close password recovery"
                    onClick={closeRecovery}
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  >
                    <span aria-hidden="true" className="block text-xl leading-none">×</span>
                  </button>
                </div>
                <form onSubmit={onRecoverySubmit} className="border-t border-slate-100 px-6 py-5 space-y-4" noValidate>
                  <div>
                    <label htmlFor="recovery-username" className="block text-sm font-semibold text-[#0A2540] mb-2">
                      Username <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                      <input
                        id="recovery-username"
                        autoFocus
                        type="text"
                        autoComplete="username"
                        value={recovery.username}
                        onChange={(e) => {
                          setRecovery((v) => ({ ...v, username: e.target.value }));
                          if (recoveryErrors.username) setRecoveryErrors((v) => ({ ...v, username: undefined }));
                        }}
                        placeholder="jsmith"
                        className={`w-full rounded-xl border pl-11 pr-4 py-3 text-[#0A2540] placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all ${
                          recoveryErrors.username
                            ? "border-rose-300 focus:ring-rose-200 focus:border-rose-400"
                            : "border-slate-200 focus:ring-emerald-500 focus:border-emerald-500"
                        }`}
                      />
                    </div>
                    {recoveryErrors.username && <p className="mt-1.5 text-xs text-rose-600 font-medium">{recoveryErrors.username}</p>}
                  </div>
                  <div>
                    <label htmlFor="recovery-email" className="block text-sm font-semibold text-[#0A2540] mb-2">
                      Email <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                      <input
                        id="recovery-email"
                        type="email"
                        autoComplete="email"
                        value={recovery.email}
                        onChange={(e) => {
                          setRecovery((v) => ({ ...v, email: e.target.value }));
                          if (recoveryErrors.email) setRecoveryErrors((v) => ({ ...v, email: undefined }));
                        }}
                        placeholder="you@pharmacy.co.uk"
                        className={`w-full rounded-xl border pl-11 pr-4 py-3 text-[#0A2540] placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all ${
                          recoveryErrors.email
                            ? "border-rose-300 focus:ring-rose-200 focus:border-rose-400"
                            : "border-slate-200 focus:ring-emerald-500 focus:border-emerald-500"
                        }`}
                      />
                    </div>
                    {recoveryErrors.email && <p className="mt-1.5 text-xs text-rose-600 font-medium">{recoveryErrors.email}</p>}
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={closeRecovery}
                      disabled={recoveryLoading}
                      className="rounded-xl border border-slate-200 px-5 py-3 text-sm font-semibold text-[#0A2540] hover:bg-slate-50 disabled:opacity-60"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={recoveryLoading}
                      className="inline-flex items-center gap-2 rounded-xl bg-[#0A2540] px-5 py-3 text-sm font-semibold text-white hover:bg-[#0F365C] disabled:opacity-60"
                    >
                      {recoveryLoading ? "Submitting..." : "Submit"}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="px-8 py-10 text-center">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
                  <MailCheck className="h-8 w-8 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-[#0A2540] mb-3">Password sent to your email</h2>
                <p className="mx-auto max-w-sm text-sm leading-relaxed text-slate-600">
                  We've sent a new password to <span className="font-semibold text-[#0A2540]">{recovery.email}</span>. Check your inbox (and spam folder) to sign back in.
                </p>
                <button
                  type="button"
                  onClick={closeRecovery}
                  className="mt-7 rounded-xl bg-[#0A2540] px-6 py-3 text-sm font-semibold text-white hover:bg-[#0F365C]"
                >
                  Got it
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}
